import type { SortingState } from '@tanstack/react-table';
import type { InventoryConfiguration } from '../../api/inventories/types';

export type { InventoryConfiguration };

export interface InventoryImage {
  signed_id: string;
  url: string;
  filename: string;
}

export interface Inventory {
  id: number;
  name: string;
  active: boolean;
  category: string | null;
  location: string | null;
  recommended_retail_price: number | null;
  cost_to_company: number | null;
  quantity: number | null;
  container_unit: number | null;
  images: InventoryImage[];
  inventory_configurations?: InventoryConfiguration[];
  created_at: string;
  updated_at: string;
}

export type InventoryStatus = 'active' | 'inactive';

export const INVENTORY_STATUS_VALUES = ['active', 'inactive'] as const;

export interface InventoryTab {
  key: InventoryStatus;
  label: string;
}

export interface TabsTranslations {
  active: string;
  inactive: string;
}

export interface InventoryCommonTranslations {
  status?: {
    active?: string;
    inactive?: string;
  };
  overview?: {
    title?: string;
    name?: string;
    category?: string;
    location?: string;
    retail_price?: string;
    cost_price?: string;
  };
  default_inventory?: {
    title?: string;
    tooltip?: string;
    configuration?: string;
    ratio?: string;
    quantity?: string;
    units?: string;
    scales_with?: string;
    format?: string;
    multiplied_by?: string;
  };
  footer?: {
  };
}

export interface InventoryPreviewTranslations {
  header?: {
    view_item_details?: string;
    item_details?: string;
  };
  footer?: {
    edit?: string;
  };
  loading?: string;
}

export interface CreateModalTranslations {
  title?: string;
  non_default?: {
    title?: string;
    description?: string;
  };
  default?: {
    title?: string;
    description?: string;
  };
  next?: string;
}

export interface InventoryTranslations {
  tabs?: TabsTranslations;
  header?: {
    total_unfiltered?: string;
  };
  search?: {
    placeholder?: string;
    aria_label?: string;
  };
  common?: InventoryCommonTranslations;
  preview?: InventoryPreviewTranslations;
  create_modal?: CreateModalTranslations;
}

export interface InventoriesTabsProps {
  currentStatus: InventoryStatus;
  onStatusChange: (status: InventoryStatus) => void;
  disabled?: boolean;
  translations?: TabsTranslations;
  layoutId?: string;
  className?: string;
}

export interface InventoriesTableProps {
  inventories: Inventory[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  currentStatus: InventoryStatus;
  isLoading?: boolean;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onRowClick?: (inventory: Inventory) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  className?: string;
}

export interface AllInventoriesPageProps {
  currentStatus: InventoryStatus;
  totalCount: number;
  onStatusChange: (status: InventoryStatus) => void;
  translations?: InventoryTranslations;
  isLoading: boolean;
  isFetching: boolean;
  inventories: Inventory[];
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onRowClick?: (inventory: Inventory) => void;
  onCreateNew?: () => void;
  /** RBAC: when false, hide write affordances (the "New inventory item" button). */
  canWrite?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

export const DEFAULT_TAB_LABELS: Record<InventoryStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export interface InventoryOptions {
  categories: string[];
  locations: string[];
}

export interface InventoryEditProps {
  inventory: Inventory;
  options: InventoryOptions;
}
